/**
 * Session State Manager
 * 
 * Centralized session storage management with automatic expiration.
 * Reduces redundant API calls by caching product data, prices, and other state.
 * 
 * Features:
 * - Automatic 24-hour expiration (configurable)
 * - Namespace isolation to avoid conflicts
 * - JSON serialization/deserialization
 * - Bulk operations for efficiency
 * - Cache statistics and debugging
 * 
 * Usage:
 *   SessionState.set('product-123', productData); // Default 24h expiration
 *   SessionState.set('price-abc', priceData, 3600000); // Custom 1h expiration
 *   const data = SessionState.get('product-123');
 *   SessionState.clear('product-123');
 *   SessionState.clearExpired(); // Clean up expired entries
 */

console.log('💾 js-session-state.js LOADED', { defaultTTL: '24 hours' });

class SessionStateManager {
  constructor() {
    this.namespace = 'rr_cache_';
    this.defaultTTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    this.statsKey = this.namespace + 'stats';
    
    // Initialize stats
    this.initializeStats();
    
    // Clean up expired entries on load
    this.clearExpired();
  }

  /**
   * Initialize or load cache statistics
   */
  initializeStats() {
    const existing = this.getRawItem(this.statsKey);
    if (existing) {
      this.stats = existing;
    } else {
      this.stats = {
        hits: 0,
        misses: 0,
        sets: 0,
        clears: 0,
        lastCleanup: Date.now()
      };
      this.saveStats();
    }
  }

  /**
   * Save cache statistics
   */
  saveStats() {
    try {
      sessionStorage.setItem(this.statsKey, JSON.stringify(this.stats));
    } catch (error) {
      console.warn('⚠️ Failed to save cache stats:', error);
    }
  }

  /**
   * Get item from cache
   * @param {string} key - Cache key
   * @returns {any|null} Cached value or null if not found/expired
   */
  get(key) {
    const fullKey = this.namespace + key;
    const item = this.getRawItem(fullKey);
    
    if (!item) {
      this.stats.misses++;
      this.saveStats();
      return null;
    }
    
    // Check expiration
    if (item.expiresAt && Date.now() > item.expiresAt) {
      this.clear(key);
      this.stats.misses++;
      this.saveStats();
      return null;
    }
    
    this.stats.hits++;
    this.saveStats();
    return item.value;
  }

  /**
   * Set item in cache
   * @param {string} key - Cache key
   * @param {any} value - Value to cache
   * @param {number} ttl - Time to live in milliseconds (default: 24 hours)
   */
  set(key, value, ttl = this.defaultTTL) {
    const fullKey = this.namespace + key;
    const item = {
      value,
      createdAt: Date.now(),
      expiresAt: Date.now() + ttl
    };
    
    try {
      sessionStorage.setItem(fullKey, JSON.stringify(item));
      this.stats.sets++;
      this.saveStats();
    } catch (error) {
      // Handle quota exceeded
      if (error.name === 'QuotaExceededError') {
        console.warn('⚠️ Session storage quota exceeded, clearing expired entries');
        this.clearExpired();
        
        // Try again after cleanup
        try {
          sessionStorage.setItem(fullKey, JSON.stringify(item));
          this.stats.sets++;
          this.saveStats();
        } catch (retryError) {
          console.error('❌ Failed to set cache after cleanup:', retryError);
        }
      } else {
        console.error('❌ Failed to set cache:', error);
      }
    }
  }

  /**
   * Get raw item from session storage (internal)
   */
  getRawItem(fullKey) {
    try {
      const raw = sessionStorage.getItem(fullKey);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (error) {
      console.warn('⚠️ Failed to parse cache item:', fullKey, error);
      sessionStorage.removeItem(fullKey);
      return null;
    }
  }

  /**
   * Clear specific item from cache
   * @param {string} key - Cache key
   */
  clear(key) {
    const fullKey = this.namespace + key;
    sessionStorage.removeItem(fullKey);
    this.stats.clears++;
    this.saveStats();
  }

  /**
   * Clear all items with namespace prefix
   */
  clearAll() {
    const keys = this.getAllKeys();
    keys.forEach(key => sessionStorage.removeItem(this.namespace + key));
    this.stats.clears += keys.length;
    this.saveStats();
    console.log('🗑️ Cleared all cache entries:', keys.length);
  }

  /**
   * Clear expired entries
   * Returns number of entries cleared
   */
  clearExpired() {
    const now = Date.now();
    const keys = this.getAllKeys();
    let cleared = 0;
    
    keys.forEach(key => {
      const item = this.getRawItem(this.namespace + key);
      if (item && item.expiresAt && now > item.expiresAt) {
        sessionStorage.removeItem(this.namespace + key);
        cleared++;
      }
    });
    
    if (cleared > 0) {
      console.log('🗑️ Cleared expired cache entries:', cleared);
      this.stats.clears += cleared;
      this.stats.lastCleanup = now;
      this.saveStats();
    }
    
    return cleared;
  }

  /**
   * Get all cache keys (without namespace prefix)
   */
  getAllKeys() {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(this.namespace) && key !== this.statsKey) {
        keys.push(key.substring(this.namespace.length));
      }
    }
    return keys;
  }

  /**
   * Check if key exists and is not expired
   */
  has(key) {
    return this.get(key) !== null;
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const keys = this.getAllKeys();
    const totalSize = keys.reduce((size, key) => {
      const item = sessionStorage.getItem(this.namespace + key);
      return size + (item ? item.length : 0);
    }, 0);
    
    return {
      ...this.stats,
      entries: keys.length,
      size: `${(totalSize / 1024).toFixed(2)} KB`,
      hitRate: this.stats.hits + this.stats.misses > 0 
        ? `${((this.stats.hits / (this.stats.hits + this.stats.misses)) * 100).toFixed(1)}%`
        : '0%'
    };
  }

  /**
   * Print cache statistics to console
   */
  printStats() {
    console.log('📊 Session State Cache Statistics:', this.getStats());
  }

  /**
   * Bulk set multiple items
   * @param {Object} items - Object with key-value pairs
   * @param {number} ttl - Time to live (optional)
   */
  bulkSet(items, ttl = this.defaultTTL) {
    Object.entries(items).forEach(([key, value]) => {
      this.set(key, value, ttl);
    });
  }

  /**
   * Bulk get multiple items
   * @param {string[]} keys - Array of cache keys
   * @returns {Object} Object with key-value pairs (missing keys omitted)
   */
  bulkGet(keys) {
    const result = {};
    keys.forEach(key => {
      const value = this.get(key);
      if (value !== null) {
        result[key] = value;
      }
    });
    return result;
  }
}

// Create global singleton instance
if (!window.SessionState) {
  window.SessionState = new SessionStateManager();
  
  console.log('✅ SessionState initialized', {
    namespace: window.SessionState.namespace,
    defaultTTL: '24 hours',
    stats: 'window.SessionState.printStats()',
    clearAll: 'window.SessionState.clearAll()'
  });
}
